import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { nbPerPageLabelTiny } from 'referencial/constants/ReferencialConstants'
import i18n from 'simple-react-i18n'
import { getFileExtension, getFileInfo, getFormatedFileName, isFileOfSamplePoint } from 'utils/FileUtils'
import useSandreList from 'utils/customHook/useSandreList'
import { isNil } from 'lodash'
import moment from 'moment'
import ToastrAction from 'toastr/actions/ToastrAction'
import StationAction from 'station/actions/StationAction'
import { getSiteUrl } from 'utils/mapUtils/SiteTypes'
import { STATION_NAME_ASSOCIATION } from 'station/constants/StationConstants'
import { getDate } from 'utils/DateUtil'
import { Button, Card, CardContent, Dialog, DialogActions, DialogContent, Grid } from '@mui/material'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import Input from 'components/forms/Input'
import Select from 'components/forms/Select'
import SimpleDatePicker from 'components/forms/SimpleDatePicker'
import Checkbox from 'components/forms/Checkbox'
import { CardTable } from 'components/datatable/NewTable'
import DtoFile from 'components/file/dto/DtoFile'
import DtoQualitometer from 'quality/dto/DtoQualitometer'
import DtoPiezometer from 'piezometry/dto/DtoPiezometer'
import DtoHydrometricStation from 'hydrometry/dto/DtoHydrometricStation'
import PluviometerDto from 'pluviometry/dto/PluviometerDto'
import DtoInstallation from 'installation/dto/installation/DtoInstallation'
import DtoDistributionUnit from 'distributionUnit/dto/DtoDistributionUnit'
import { ProductionUnitShape } from 'productionUnit/shapes/ProductionUnitShapes'
import ResourceDto from 'resource/dto/ResourceDto'
import { hasValue } from 'utils/NumberUtil'
import { ShapeSamplePoint } from 'station/shapes/StationShapes'

const UpdateFileDialog = ({
    isOpen = false,
    onClose = () => {},

    fileName,
    files = [],
    station,
    stationType,
    setLoaded,
    linkSamplePoints,
}) => {
    const [name, setName] = useState('')
    const [fileType, setFileType] = useState(0)
    const [date, setDate] = useState('')
    const [samplePoint, setSamplePoint] = useState({})
    const [active, setActive] = useState('0')
    const [shared, setShared] = useState(false)

    const {
        associatedSites,
    } = useSelector(store => ({
        associatedSites: store.StationReducer.associatedSites,
    }), shallowEqual)

    const dispatch = useDispatch()

    const file = useMemo(() => files.find(f => f.name === fileName), [files, fileName])

    const {
        sandreConstant,
        fileTypeName,
    } = getFileInfo(file)

    const fileTypes = useSandreList(sandreConstant)

    useEffect(() => {
        if (isNil(file)) return

        const ft = fileTypes?.find(t => t.mnemonique === file.type || t.name === file.type)?.code

        setName(file.shortName)
        setFileType(ft)
        setDate(file.date)
        setSamplePoint(linkSamplePoints?.find(sp => isFileOfSamplePoint(file, sp))?.idPoint)
        setActive(file.active === '1')
        setShared(file.shared)
    }, [file])

    const saveFile = () => {
        const now = moment().valueOf()
        const activeInt = active ? 1 : 0
        const ft = fileTypes.find(t => t.code === fileType)
        const point = linkSamplePoints?.find(sp => sp.idPoint === samplePoint)
        if (!isNil(point) && !hasValue(point.identifiant)) {
            dispatch(ToastrAction.warning(i18n.samplingPointWithoutIdCantBeAffected))
            return
        }
        const formattedName = getFormatedFileName(
            station.code,
            activeInt,
            name,
            getFileExtension(file.name),
            ft.mnemonique || ft.name,
            date ?? now,
            stationType,
            shared,
            point,
        )
        if (file.name !== formattedName && files.some(f => f.name === formattedName)) {
            dispatch(ToastrAction.warning(i18n.nameAlreadyExist))
            return
        }

        const formattedFile = {
            ...file,
            newName: formattedName,
        }

        dispatch(StationAction.updateFile(formattedFile)).then(() => {
            setLoaded()
            onClose()
        })
    }

    const formattedAssociatedSites = useMemo(() => {
        return associatedSites.filter(as => as.code === as.stationLinkedCode).map(d => ({
            stationType: (
                <img
                    src={getSiteUrl(STATION_NAME_ASSOCIATION[d.typeName])}
                    style={{ maxHeight: '30px' }}
                />
            ),
            code: d.code,
            name: d.stationLinkedName,
        }))
    }, [associatedSites])

    const samplePoints = useMemo(() => {
        if (stationType !== 'quality') return []
        return linkSamplePoints
            ?.filter(sp => !isNil(sp.identifiant) || !isNil(sp.name))
            .map(sp => ({
                id: sp.idPoint,
                label: `${isNil(sp.identifiant) ? '' : `[${sp.identifiant}] `}${sp.name}${isNil(sp.startDate) ? '' : ` (${getDate(sp.startDate)})`}`,
            }))
    }, [station, stationType])

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth='lg'
        >
            <DefaultDialogTitle
                title={`${i18n.update}: ${file?.shortName ?? i18n.unknown}.${getFileExtension(file?.name)}`}
                onClose={onClose}
            />
            <DialogContent>
                <Card>
                    <CardContent sx={{ overflow: 'hidden' }}>
                        <Grid container rowSpacing={0.5} columnSpacing={1} alignItems='center'>
                            <Grid size={12}>
                                <Input
                                    title={i18n.name}
                                    value={name}
                                    onChange={setName}
                                />
                            </Grid>
                            <Grid size={12}>
                                <Select
                                    label={fileTypeName}
                                    options={fileTypes}
                                    value={fileType}
                                    onChange={setFileType}
                                    noNullValue
                                    noSort
                                    keyLabel='name'
                                />
                            </Grid>
                            <Grid size={3}>
                                <SimpleDatePicker
                                    label={i18n.date}
                                    value={date}
                                    onChange={setDate}
                                />
                            </Grid>
                            <Grid size={3}>
                                <Checkbox
                                    onChange={setActive}
                                    checked={active}
                                    label={i18n.active}
                                />
                            </Grid>
                            <Grid size={6}>
                                {stationType === 'quality' && (
                                    <Select
                                        label={i18n.samplePoint}
                                        options={samplePoints}
                                        value={samplePoint}
                                        onChange={setSamplePoint}
                                        noSort
                                    />
                                )}
                            </Grid>
                            <Grid size={8}>
                                <CardTable
                                    title={i18n.sharedWithOtherStations}
                                    rows={formattedAssociatedSites}
                                    headers={['stationType', 'code', 'name']}
                                    rowsPerPageOptions={nbPerPageLabelTiny}
                                />
                            </Grid>
                            <Grid size={4}>
                                <Checkbox
                                    onChange={setShared}
                                    checked={shared}
                                    label={i18n.sharedWithOtherStations}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={saveFile}
                    variant='contained'
                    color='primary'
                    disabled={isNil(name) || isNil(fileType)}
                >
                    {i18n.validate}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

UpdateFileDialog.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    fileName: PropTypes.string,
    files: PropTypes.arrayOf(PropTypes.instanceOf(DtoFile)),
    station: PropTypes.oneOfType([
        PropTypes.instanceOf(DtoQualitometer),
        PropTypes.instanceOf(DtoPiezometer),
        PropTypes.instanceOf(DtoHydrometricStation),
        PropTypes.instanceOf(PluviometerDto),
        PropTypes.instanceOf(DtoInstallation),
        PropTypes.instanceOf(DtoDistributionUnit),
        ProductionUnitShape,
        PropTypes.instanceOf(ResourceDto),
    ]),
    stationType: PropTypes.string,
    setLoaded: PropTypes.func,
    linkSamplePoints: PropTypes.arrayOf(ShapeSamplePoint),
}

export default UpdateFileDialog